import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Space, 
  Tag, 
  Popconfirm, 
  message, 
  Drawer,
  Checkbox,
  Row,
  Col,
  Typography,
  Badge,
  Tooltip,
  Breadcrumb,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  UsergroupAddOutlined,
  TeamOutlined,
  HomeOutlined,
  SearchOutlined,
  UserOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import PageHeader from './PageHeader';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import groupService from '../services/groupService';
import userService from '../services/userService';

const { Option } = Select;
const { TextArea } = Input;

const { Text } = Typography;

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [form] = Form.useForm();
  const [availablePrivileges, setAvailablePrivileges] = useState({
    all: [],
    categorized: {}
  });

  useEffect(() => {
    loadGroups();
    loadPrivileges();
  }, []);

  const loadPrivileges = async () => {
    try {
      console.log('Attempting to load privileges...');
      const result = await groupService.getAllPrivileges();
      console.log('getAllPrivileges result:', result);
      
      if (result.success) {
        console.log('Loaded privileges successfully:', result.data);
        const privilegesData = result.data?.data || result.data || { all: [], categorized: {} };
        console.log('Setting privileges data:', privilegesData);
        setAvailablePrivileges(privilegesData);
      } else {
        console.error('Failed to load privileges:', result.message);
        message.error('Failed to load privileges: ' + result.message);
        // Set empty structure as fallback
        setAvailablePrivileges({ all: [], categorized: {} });
      }
    } catch (error) {
      console.error('Error loading privileges:', error);
      message.error('Failed to load privileges: ' + error.message);
      // Set empty structure as fallback
      setAvailablePrivileges({ all: [], categorized: {} });
    }
  };

  const loadGroups = async () => {
    setLoading(true);
    try {
      // Call the real API
      const result = await groupService.getAllGroups();
      console.log('loadGroups result:', result);
      
      if (result.success) {
        console.log('Groups data from API:', result.data);
        // Transform the data to match table format
        const transformedGroups = result.data?.map((group, index) => {
          console.log(`Processing group ${index}:`, group);
          return {
            id: group._id || index,
            name: group?.groupName || 'Unknown Group',
            description: group?.description || 'No description',
            memberCount: group?.memberCount || 0,
            status: group?.status || 'active',
            privileges: group?.privileges || [],
            createdAt: group?.createdAt ? new Date(group.createdAt).toLocaleDateString() : 'Unknown',
            rawGroup: group // Keep original group data for reference
          };
        });
        console.log('Transformed groups:', transformedGroups);
        setGroups(transformedGroups);
      } else {
        message.error('Failed to load groups: ' + result.message);
        setGroups([]);
      }
    } catch (error) {
      console.error('Error loading groups:', error);
      message.error('Failed to load groups.');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGroup = () => {
    setEditingGroup(null);
    form.resetFields();
    // Clear any selected privileges when adding new group
    form.setFieldsValue({
      privileges: []
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    form.resetFields();
    setEditingGroup(null);
    setIsModalVisible(false);
  };

  const handleEditGroup = (group) => {
    console.log('handleEditGroup called with:', group);
    setEditingGroup(group);
    
    // Set form values with the group data, ensuring privileges are properly set
    const formValues = {
      name: group.name,
      description: group.description,
      privileges: group.privileges || []  // Ensure privileges array is set
    };
    console.log('Setting form values:', formValues);
    form.setFieldsValue(formValues);
    setIsModalVisible(true);
  };

  const handleDeleteGroup = (groupId) => {
    Modal.confirm({
      title: 'Delete Group',
      content: 'Are you sure you want to delete this group? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          // Call the actual API to delete the group
          const result = await groupService.deleteGroup(groupId);
          
          if (result.success) {
            // Remove from local state after successful API call
            const updatedGroups = groups.filter(group => group.id !== groupId);
            setGroups(updatedGroups);
            message.success('Group deleted successfully');
          } else {
            message.error(result.message || 'Failed to delete group');
          }
        } catch (error) {
          console.error('Error deleting group:', error);
          message.error('Failed to delete group');
        }
      }
    });
  };

  const handleSaveGroup = async (values) => {
    setSaveLoading(true);
    try {
      // Prepare data for API - map form field 'name' to API field 'groupName'
      const groupData = {
        groupName: values.name,
        description: values.description,
        status: values.status || 'active',
        privileges: values.privileges || []
      };

      if (editingGroup) {
        // Update existing group via API
        const result = await groupService.updateGroup(editingGroup.id, groupData);
        
        if (result.success) {
          message.success('Group updated successfully');
          // Reload groups from API to get fresh data
          loadGroups();
        } else {
          message.error(result.message || 'Failed to update group');
          setSaveLoading(false);
          return;
        }
      } else {
        // Create new group via API
        const result = await groupService.createGroup(groupData);
        
        if (result.success) {
          message.success('Group created successfully');
          // Reload groups from API to get fresh data
          loadGroups();
        } else {
          message.error(result.message || 'Failed to create group');
          setSaveLoading(false);
          return;
        }
      }
      
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error saving group:', error);
      message.error('Failed to save group');
    } finally {
      setSaveLoading(false);
    }
  };

  const getPermissionColor = (permission) => {
    // Simple, subtle color scheme
    return 'default';
  };

  const getPermissionLabel = (permission) => {
    // Format the permission name for display
    return permission
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchText.toLowerCase()) ||
    group.description.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Group Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Members',
      dataIndex: 'memberCount',
      key: 'memberCount',
      render: (count) => (
        <Space>
          <UserOutlined />
          <span>{count}</span>
        </Space>
      ),
    },
    {
      title: 'Privileges',
      dataIndex: 'privileges',
      key: 'permissions',
      width: 280,
      render: (permissions) => {
        if (!permissions || permissions.length === 0) {
          return <Text type="secondary">No privileges</Text>;
        }
        
        const displayPermissions = permissions.slice(0, 2);
        const remainingCount = permissions.length - 2;
        
        const allPrivilegesTooltip = (
          <div style={{ maxWidth: 280 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8 }}>All Privileges ({permissions.length}):</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {permissions.map(p => (
                <Tag key={p} style={{ margin: 0, fontSize: 11 }}>
                  {getPermissionLabel(p)}
                </Tag>
              ))}
            </div>
          </div>
        );
        
        return (
          <Space wrap size={[4, 4]}>
            {displayPermissions.map(permission => (
              <Tag 
                key={permission} 
                style={{ margin: 0, fontSize: 11 }}
              >
                {getPermissionLabel(permission)}
              </Tag>
            ))}
            {remainingCount > 0 && (
              <Tooltip title={allPrivilegesTooltip} placement="top" overlayStyle={{ maxWidth: 320 }}>
                <Tag 
                  style={{ margin: 0, cursor: 'pointer', fontSize: 11, color: '#666' }} 
                >
                  +{remainingCount} more
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditGroup(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteGroup(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader 
        title="Groups - Team Management" 
        subtitle="Create and manage user groups with specific permissions and privileges"
        breadcrumbs={BREADCRUMB_PATTERNS.groups}
      />

      <Card>
        <div style={{ marginBottom: '24px' }}>
          {/* Search and Actions */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
            marginBottom: '16px'
          }}>
            <Space>
              <Input
                placeholder="Search groups..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 280 }}
                allowClear
              />
              <Text type="secondary">
                {filteredGroups.length} group{filteredGroups.length !== 1 ? 's' : ''} found
              </Text>
            </Space>
            <Space>
              <Button  icon={<PlusOutlined />} onClick={handleAddGroup}>
                Create Group
              </Button>
              <Button>Export</Button>
            </Space>
          </div>
        </div>

        {/* Groups Table */}
        <Table
          columns={columns}
          dataSource={filteredGroups}
          loading={loading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} groups`,
            pageSize: 10,
          }}
        />

        {/* Add/Edit Group Modal */}
        <Modal
          title={editingGroup ? 'Edit Group' : 'Create New Group'}
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={800}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSaveGroup}
            disabled={saveLoading}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Group Name"
                  rules={[
                    { required: true, message: 'Please enter group name' },
                    { min: 2, message: 'Group name must be at least 2 characters' },
                    { max: 50, message: 'Group name cannot exceed 50 characters' }
                  ]}
                >
                  <Input placeholder="e.g., Administrators, Managers, Viewers" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: 'Please select status' }]}
                  initialValue="active"
                >
                  <Select>
                    <Option value="active">
                      <Space>
                        <Badge status="success" />
                        Active
                      </Space>
                    </Option>
                    <Option value="inactive">
                      <Space>
                        <Badge status="error" />
                        Inactive
                      </Space>
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="description"
              label="Description"
              rules={[
                { required: true, message: 'Please enter description' },
                { max: 500, message: 'Description cannot exceed 500 characters' }
              ]}
            >
              <TextArea 
                rows={3} 
                placeholder="Describe the purpose and responsibilities of this group"
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Form.Item
              name="privileges"
              label={<span>Privileges</span>}
              rules={[{ required: true, message: 'Please select at least one privilege' }]}
            >
              <Checkbox.Group style={{ width: '100%' }}>
                <div style={{ 
                  maxHeight: 350, 
                  overflowY: 'auto', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: 8, 
                  padding: 16,
                  background: '#fafafa'
                }}>
                  {availablePrivileges.categorized && Object.entries(availablePrivileges.categorized).map(([category, privileges]) => {
                    if (!privileges || privileges.length === 0) return null;
                    
                    return (
                      <div key={category} style={{ marginBottom: 16 }}>
                        <div style={{ 
                          padding: '6px 12px', 
                          borderRadius: 4, 
                          marginBottom: 8,
                          background: '#f0f0f0',
                          borderLeft: '3px solid #888'
                        }}>
                          <Text strong>{category}</Text>
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>({privileges.length})</Text>
                        </div>
                        <Row gutter={[8, 8]} style={{ paddingLeft: 8 }}>
                          {privileges.map(privilege => (
                            <Col xs={24} sm={12} key={privilege}>
                              <Checkbox value={privilege} style={{ fontSize: 13 }}>
                                {privilege.replace(/_/g, ' ')}
                              </Checkbox>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    );
                  })}
                </div>
              </Checkbox.Group>
            </Form.Item>

            <Divider style={{ margin: '16px 0' }} />

            <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
              <Space>
                <Button onClick={handleCancel} disabled={saveLoading}>
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={saveLoading}
                  icon={editingGroup ? <EditOutlined /> : <PlusOutlined />}
                >
                  {editingGroup ? 'Update' : 'Create'} Group
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default Groups;